// Shared 1 / X / 2 back(blue)/lay(pink) live-odds strip — used by BOTH the
// cricket/football/tennis LOBBY cards (games/cricket/Page.tsx) and the home
// Cricket hero list (pages/Home.tsx). Fed by a `/cricket/state` entry for a
// match. Every cell ALWAYS keeps its colour (blue back / pink lay) even with no
// value ("–"); a moved price pulses yellow; a suspended market/runner shows the
// SAME colour a shade darker + a lock icon. Display-only.
import { useState, useEffect, useRef } from 'react';
import Icon from '@baaligames/shared-ui/components/Icon';

// Pulse a cell yellow for a beat whenever its price value changes.
function useFlash(value: string | null | undefined) {
  const [flash, setFlash] = useState(false);
  const prev = useRef(value);
  useEffect(() => {
    if (prev.current !== value && value != null && value !== '' && value !== '-' && prev.current != null) {
      setFlash(true);
      const tm = setTimeout(() => setFlash(false), 550);
      prev.current = value;
      return () => clearTimeout(tm);
    }
    prev.current = value;
  }, [value]);
  return flash;
}

function OddsCell({ price, suspended, kind }: { price?: string | null; suspended?: boolean; kind: 'back' | 'lay' }) {
  const flash = useFlash(suspended ? null : price);
  if (suspended) {
    const cls = kind === 'back'
      ? 'bg-blue-300/80 dark:bg-blue-900/70 border-blue-400/70 dark:border-blue-700 text-blue-800/70 dark:text-blue-200/60'
      : 'bg-pink-300/80 dark:bg-pink-900/70 border-pink-400/70 dark:border-pink-700 text-pink-800/70 dark:text-pink-200/60';
    return (
      <div className={`flex-1 h-[20px] sm:h-7 rounded border flex items-center justify-center ${cls}`}>
        <Icon name="lock" size={11} />
      </div>
    );
  }
  const cls = kind === 'back'
    ? 'bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-500/40'
    : 'bg-pink-100 dark:bg-pink-500/20 text-pink-700 dark:text-pink-300 border-pink-200 dark:border-pink-500/40';
  const has = price != null && price !== '' && price !== '-';
  return (
    <div className={`flex-1 h-[20px] sm:h-7 rounded border flex items-center justify-center font-bold text-[11px] sm:text-[12px] tabular-nums transition-shadow ${cls} ${flash ? 'ring-2 ring-yellow-400 dark:ring-yellow-300' : ''}`}>
      {has ? price : '–'}
    </div>
  );
}

export default function LiveOddsRow({ state, team1, team2 }: { state: any; team1: string; team2: string }) {
  const market = state?.markets?.find((mk: any) => mk?.marketName === 'Match Odds' || mk?.marketType === 'match_odds');
  const runners: any[] = market?.runners || [];
  const marketSuspended = market?.status === 'suspended';
  const norm = (s: string) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  const findR = (team: string) => {
    const n = norm(team);
    return runners.find((r) => norm(r.name) === n)
        || runners.find((r) => n && (norm(r.name).includes(n) || n.includes(norm(r.name))));
  };
  const rDraw = runners.find((r) => /draw/i.test(r.name));
  const r1 = runners.length ? (findR(team1) || runners[0]) : null;
  const r2 = runners.length ? (findR(team2) || (runners.length > 2 ? runners[runners.length - 1] : runners[1])) : null;
  const cellFor = (r: any): { suspended: boolean; b: string | null; l: string | null } => {
    if (marketSuspended || (r && r.suspended)) return { suspended: true, b: null, l: null };
    const b = r?.back?.[0]?.price, l = r?.lay?.[0]?.price;
    const hasB = b != null && b !== '' && b !== '-';
    const hasL = l != null && l !== '' && l !== '-';
    return { suspended: false, b: hasB ? String(b) : null, l: hasL ? String(l) : null };
  };
  const cols = [
    { label: '1', c: cellFor(r1) },
    { label: 'X', c: cellFor(rDraw) },
    { label: '2', c: cellFor(r2) },
  ];
  return (
    <div className="mt-[3px] sm:mt-1.5 grid grid-cols-3 gap-1.5 sm:gap-2">
      {cols.map((col) => (
        <div key={col.label}>
          <div className="text-center text-[7.5px] sm:text-[8.5px] font-black uppercase tracking-widest text-arena-500 dark:text-neon-200/55 leading-none mb-0 sm:mb-0.5">{col.label}</div>
          <div className="flex gap-[3px] sm:gap-1">
            <OddsCell kind="back" suspended={col.c.suspended} price={col.c.b} />
            <OddsCell kind="lay"  suspended={col.c.suspended} price={col.c.l} />
          </div>
        </div>
      ))}
    </div>
  );
}
